import React, {useEffect, useState} from 'react'
import {Navigate, useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {useAuth} from '../../../modules/auth'
import {shouldDisplayFonctionnalite} from '../../../../components/helpers'
import {LoadingData} from '../../../../components/LoadingData'
import {ModeleSavLigne} from '../../../../components/sav/Models'
import {
  creerSavDemandeRemplacement,
  savErreur,
  transitionSavDossier,
  updateSavDossier,
  updateSavLigne,
} from '../../../../components/sav/requests'
import {formatSavDate, formatSavMontant, SAV_ORIGINES} from '../../../../components/sav/libelles'
import SavDossierHeader from '../components/SavDossierHeader'
import SavFilModifications from '../components/SavFilModifications'
import {useSavDossier} from '../components/useSavDossier'
import {useSavReferentiels} from '../components/useSavReferentiels'
import SavInformerClient, {SavInformerClientModal} from '../components/SavInformerClient'
import SavDemandeRemplacement from '../components/SavDemandeRemplacement'

const nombre = (valeur?: string | null) =>
  valeur === null || valeur === undefined || valeur === ''
    ? null
    : Number(String(valeur).replace(',', '.'))

// Montant accordé d'une pièce, enregistré en quittant le champ
const SavMontantLigne = ({
  dossierId,
  ligne,
  modifiable,
  onMaj,
}: {
  dossierId: number
  ligne: ModeleSavLigne
  modifiable: boolean
  onMaj: (d: any) => void
}) => {
  const [valeur, setValeur] = useState(ligne.montantAccordeHt ?? '')

  useEffect(() => setValeur(ligne.montantAccordeHt ?? ''), [ligne.montantAccordeHt])

  const enregistrer = () => {
    if ((ligne.montantAccordeHt ?? '') === valeur) return
    updateSavLigne(dossierId, ligne.id, {montantAccordeHt: valeur === '' ? null : valeur})
      .then(onMaj)
      .catch((e) => {
        toast.error(savErreur(e))
        setValeur(ligne.montantAccordeHt ?? '')
      })
  }

  const depasse =
    nombre(valeur) !== null && ligne.prixVenteHt && nombre(valeur)! > Number(ligne.prixVenteHt)
  return (
    <div>
      <input
        className={`form-control form-control-sm w-125px ${depasse ? 'is-invalid' : ''}`}
        inputMode='decimal'
        disabled={!modifiable}
        placeholder={ligne.prixVenteHt ?? '0,00'}
        value={valeur}
        onChange={(e) => setValeur(e.target.value)}
        onBlur={enregistrer}
      />
      {depasse && <div className='text-danger fs-8'>Au-delà du prix de vente</div>}
    </div>
  )
}

// Écran 8 : solution et clôture
const SavSolutionPage = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const referentiels = useSavReferentiels()
  const {detail, erreur, maj, messagePropose, fermerMessage} = useSavDossier(id)
  const [avoir, setAvoir] = useState({
    refFactureExterne: '',
    numeroAvoirAtemo: '',
    dateAvoir: '',
    avoirTransmisCompta: false,
  })
  const [refAtemo, setRefAtemo] = useState('')
  const [mode, setMode] = useState<'ATEMO' | 'TELEVENTE'>('ATEMO')
  const [envoi, setEnvoi] = useState(false)

  const dossier = detail?.dossier
  useEffect(() => {
    if (!dossier) return
    setAvoir({
      refFactureExterne: dossier.refFactureExterne ?? '',
      numeroAvoirAtemo: dossier.numeroAvoirAtemo ?? '',
      dateAvoir: dossier.dateAvoir ? dossier.dateAvoir.slice(0, 10) : '',
      avoirTransmisCompta: dossier.avoirTransmisCompta,
    })
    setRefAtemo(dossier.refCommandeRemplacementAtemo ?? '')
    if (dossier.modeEchange) setMode(dossier.modeEchange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossier?.id, dossier?.statut.code])

  if (erreur) return <div className='alert alert-danger'>{erreur}</div>
  if (!detail || !dossier || !referentiels) return <LoadingData />
  if (!['A_SOLDER', 'COMMANDE'].includes(dossier.statut.code))
    return <Navigate to={`/sav/dossiers/${dossier.id}/fiche`} replace />

  const cloturer = shouldDisplayFonctionnalite(currentUser, 'SAV_CLOTURER')
  const deroger = shouldDisplayFonctionnalite(currentUser, 'SAV_DEROGER')
  const aSolder = dossier.statut.code === 'A_SOLDER'
  // RG16 : la demande de remplacement créée, la solution ne change plus
  const solutionModifiable = aSolder && cloturer && !dossier.clientDemandeRemplacement
  const comportement = dossier.solution?.comportement
  const total = dossier.lignes.reduce((t, l) => t + (nombre(l.montantAccordeHt) ?? 0), 0)
  const seuil = nombre(referentiels.seuilValidationAvoir)

  // RG08 : cas qui demandent la validation du responsable SAV
  const validations = [
    ...dossier.lignes
      .filter(
        (l) =>
          l.montantAccordeHt && l.prixVenteHt && Number(l.montantAccordeHt) > Number(l.prixVenteHt)
      )
      .map((l) => `${l.libellePiece} : montant supérieur au prix de vente`),
    ...dossier.lignes
      .filter((l) => l.montantAccordeHt && !l.prixVerifie)
      .map((l) => `${l.libellePiece} : prix déclaré, non vérifié`),
    ...(seuil !== null && total > seuil
      ? [`Avoir au-delà du seuil de ${formatSavMontant(seuil)} HT`]
      : []),
  ]
  // RG09 : chaque pièce retournée a une destination
  const sansDestination = dossier.lignes.filter((l) => l.retourRequis && !l.destination)

  const executer = (appel: Promise<any>, message: string, apres?: (d: any) => void) => {
    setEnvoi(true)
    appel
      .then((d) => {
        toast.success(message)
        maj(d)
        apres?.(d)
      })
      .catch((e) => toast.error(savErreur(e)))
      .finally(() => setEnvoi(false))
  }

  const choisirSolution = (solution: number) =>
    executer(updateSavDossier(dossier.id, {solution}), 'Solution enregistrée')

  const enregistrerAvoir = () =>
    updateSavDossier(dossier.id, {
      refFactureExterne: avoir.refFactureExterne,
      numeroAvoirAtemo: avoir.numeroAvoirAtemo,
      dateAvoir: avoir.dateAvoir || null,
      avoirTransmisCompta: avoir.avoirTransmisCompta,
    })

  const cloturerDossier = () => {
    const avant = comportement === 'AVOIR' ? enregistrerAvoir() : Promise.resolve(null)
    executer(
      avant.then(() => transitionSavDossier(dossier.id, 'CLOS_ACCEPTE')),
      'SAV clôturé',
      () => navigate(`/sav/dossiers/${dossier.id}/fiche`)
    )
  }

  // « Envoyer et clôturer » : avoir enregistré et dossier clos avant l'envoi du message
  const cloturerAvantMessage = async () => {
    if (sansDestination.length) throw new Error('Destination à renseigner avant la clôture.')
    if (comportement === 'AVOIR') await enregistrerAvoir()
    await transitionSavDossier(dossier.id, 'CLOS_ACCEPTE')
  }

  const passerEnCommande = () =>
    executer(
      updateSavDossier(dossier.id, {
        modeEchange: 'ATEMO',
        refCommandeRemplacementAtemo: refAtemo,
      }).then(() => transitionSavDossier(dossier.id, 'COMMANDE')),
      'Dossier en Commande'
    )

  const creerDemande = () =>
    executer(creerSavDemandeRemplacement(dossier.id), 'Demande de pièce créée pour la télévente')

  const vente =
    dossier.commande?.numCommande ??
    dossier.catalogueCommande?.numCommande ??
    dossier.refFactureExterne ??
    dossier.refCommandeExterne ??
    dossier.refTicketOdoo

  return (
    <>
      <SavDossierHeader
        detail={detail}
        onRecharger={maj}
        ac10={{etape: 'Clôture SAV', acteur: 'Interlocuteur SAV', delai: 'J + 2'}}
        action={
          <button
            className='btn btn-sm btn-light-primary'
            onClick={() => navigate(`/sav/dossiers/${dossier.id}/fiche`)}
          >
            {dossier.origine === 'HISTO_ODOO' ? 'Voir le ticket ODOO' : 'Voir la fiche du dossier'}
          </button>
        }
      />

      <div className='row g-5'>
        <div className='col-lg-8'>
          <div className='card mb-5'>
            <div className='card-header'>
              <h3 className='card-title'>Résultat du contrôle</h3>
              <div className='card-toolbar text-muted'>
                {SAV_ORIGINES[dossier.origine]} {vente}
              </div>
            </div>
            <div className='card-body'>
              <div className='table-responsive'>
                <table className='table align-middle gs-0 gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th>Référence pièce</th>
                      <th>Immatriculation</th>
                      <th>Motif</th>
                      <th>Prix de vente HT</th>
                      <th>Réclamation</th>
                      <th>Destination</th>
                      <th>Montant accordé HT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dossier.lignes.map((l) => (
                      <tr key={l.id}>
                        <td className='fw-bold'>
                          {(l.pieceEntre?.code ?? l.codePieceExterne) &&
                            `${l.pieceEntre?.code ?? l.codePieceExterne} - `}
                          {l.libellePiece}
                        </td>
                        <td>{l.immatriculation ?? '—'}</td>
                        <td>{l.motif?.libelle}</td>
                        <td>
                          {formatSavMontant(l.prixVenteHt)}
                          <div className='text-muted fs-8'>
                            {l.prixVerifie ? 'vérifié' : 'déclaré'}
                          </div>
                        </td>
                        <td>
                          {l.controleFonde === true && (
                            <span className='badge badge-light-success'>Fondée</span>
                          )}
                          {l.controleFonde === false && (
                            <span className='badge badge-light-danger'>Non fondée</span>
                          )}
                          {(l.controleFonde === null || l.controleFonde === undefined) && (
                            <span className='text-muted fs-7'>Sans contrôle</span>
                          )}
                        </td>
                        <td>
                          {l.destination ? (
                            <span
                              className={`badge ${
                                l.destination.comportement === 'DESTRUCTION'
                                  ? 'badge-light-danger'
                                  : 'badge-light-primary'
                              }`}
                            >
                              {l.destination.libelle}
                            </span>
                          ) : l.retourRequis ? (
                            <span className='text-danger fs-7'>À renseigner</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <SavMontantLigne
                            dossierId={dossier.id}
                            ligne={l}
                            modifiable={aSolder && cloturer}
                            onMaj={maj}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className='d-flex justify-content-end fs-5'>
                Total accordé : <span className='fw-bolder ms-2'>{formatSavMontant(total)} HT</span>
              </div>
              {validations.length > 0 && (
                <div className='alert alert-warning mt-4 mb-0'>
                  {validations.join(' · ')} : le montant doit être validé par le responsable SAV
                  {deroger ? ' (vous avez ce droit).' : '.'}
                </div>
              )}
              {sansDestination.length > 0 && (
                <div className='alert alert-danger mt-4 mb-0'>
                  Destination à renseigner avant la clôture :{' '}
                  {sansDestination.map((l) => l.libellePiece).join(', ')}.
                </div>
              )}
            </div>
          </div>

          <div className='card mb-5'>
            <div className='card-header'>
              <h3 className='card-title'>Quelle solution pour le client ?</h3>
            </div>
            <div className='card-body'>
              <div className='row g-4 mb-5'>
                {referentiels.solutions.map((s) => {
                  const active = dossier.solution?.id === s.id
                  return (
                    <div className='col-md-3' key={s.id}>
                      <div
                        className={`rounded p-4 h-100 ${
                          solutionModifiable ? 'cursor-pointer' : ''
                        }`}
                        style={{
                          border: `2px solid ${active ? '#50CD89' : '#E1E3EA'}`,
                          backgroundColor: active ? '#E8FFF3' : '#FFFFFF',
                        }}
                        onClick={() => solutionModifiable && !active && choisirSolution(s.id!)}
                      >
                        <div className='fw-bold'>{s.libelle}</div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {comportement === 'AVOIR' && (
                <div className='rounded p-5' style={{border: '2px dashed #50CD89'}}>
                  <h4>Émettre l'avoir</h4>
                  <ol className='text-muted'>
                    <li>Créer l'avoir dans ATEMO, sur la facture d'origine.</li>
                    <li>Reporter ici son numéro, son montant et sa date.</li>
                    <li>Le transmettre à la comptabilité.</li>
                  </ol>
                  <div className='row g-4'>
                    <div className='col-md-6'>
                      <label className='form-label required'>N° facture d'origine ATEMO</label>
                      <input
                        className='form-control form-control-solid'
                        value={avoir.refFactureExterne}
                        onChange={(e) =>
                          setAvoir((a) => ({...a, refFactureExterne: e.target.value}))
                        }
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label required'>N° d'avoir ATEMO</label>
                      <input
                        className='form-control form-control-solid'
                        value={avoir.numeroAvoirAtemo}
                        onChange={(e) =>
                          setAvoir((a) => ({...a, numeroAvoirAtemo: e.target.value}))
                        }
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label'>Montant (HT)</label>
                      <div className='form-control form-control-solid fw-bold'>
                        {formatSavMontant(total)}
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label required'>Date de l'avoir</label>
                      <input
                        type='date'
                        className='form-control form-control-solid'
                        value={avoir.dateAvoir}
                        onChange={(e) => setAvoir((a) => ({...a, dateAvoir: e.target.value}))}
                      />
                    </div>
                    <div className='col-12'>
                      <label className='form-check form-check-custom form-check-solid'>
                        <input
                          type='checkbox'
                          className='form-check-input'
                          checked={avoir.avoirTransmisCompta}
                          onChange={(e) =>
                            setAvoir((a) => ({...a, avoirTransmisCompta: e.target.checked}))
                          }
                        />
                        <span className='form-check-label'>
                          Avoir transmis à la comptabilité
                          {dossier.dateTransmissionCompta && (
                            <span className='text-muted'>
                              {' '}
                              (le {formatSavDate(dossier.dateTransmissionCompta)})
                            </span>
                          )}
                        </span>
                      </label>
                    </div>
                  </div>
                  {cloturer && (
                    <button
                      className='btn btn-light-primary mt-5'
                      disabled={envoi}
                      onClick={() => executer(enregistrerAvoir(), 'Avoir enregistré')}
                    >
                      Enregistrer l'avoir
                    </button>
                  )}
                </div>
              )}

              {comportement === 'ECHANGE' && aSolder && dossier.clientDemandeRemplacement && (
                <div className='rounded p-5 bg-light'>
                  <h4>Échange lancé par la télévente</h4>
                  <SavDemandeRemplacement dossier={dossier} />
                </div>
              )}

              {comportement === 'ECHANGE' && aSolder && !dossier.clientDemandeRemplacement && (
                <div className='rounded p-5' style={{border: '2px dashed #1B84FF'}}>
                  <h4>Lancer l'échange</h4>
                  <div className='btn-group mb-5'>
                    <button
                      className={`btn ${mode === 'ATEMO' ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setMode('ATEMO')}
                    >
                      Commande ATEMO
                    </button>
                    <button
                      className={`btn ${mode === 'TELEVENTE' ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setMode('TELEVENTE')}
                    >
                      Demande télévente
                    </button>
                  </div>
                  {mode === 'ATEMO' ? (
                    <>
                      <label className='form-label required'>
                        N° de commande de remplacement ATEMO
                      </label>
                      <input
                        className='form-control form-control-solid mb-4'
                        value={refAtemo}
                        onChange={(e) => setRefAtemo(e.target.value)}
                      />
                      <button
                        className='btn btn-success'
                        disabled={envoi || !refAtemo.trim() || !cloturer}
                        onClick={passerEnCommande}
                      >
                        Passer en Commande
                      </button>
                    </>
                  ) : (
                    <>
                      <p className='text-muted'>
                        Une demande de pièce (process AC03) est créée pour le client et liée au
                        dossier, avec une pièce par pièce fondée, au prix initialement facturé. Elle
                        arrive dans la file télévente habituelle ; le dossier passe en Commande puis
                        se clôture automatiquement au passage de la commande.
                      </p>
                      <button
                        className='btn btn-success'
                        disabled={envoi || !cloturer}
                        onClick={creerDemande}
                      >
                        Créer la demande de pièce
                      </button>
                    </>
                  )}
                </div>
              )}

              {dossier.statut.code === 'COMMANDE' && (
                <div className='rounded p-5 bg-light'>
                  <h4>Échange en cours</h4>
                  {dossier.modeEchange === 'ATEMO' ? (
                    <div>
                      Commande de remplacement ATEMO n°{' '}
                      <strong>{dossier.refCommandeRemplacementAtemo}</strong>.
                    </div>
                  ) : (
                    <SavDemandeRemplacement dossier={dossier} />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='col-lg-4'>
          <div className='card' style={{position: 'sticky', top: 90}}>
            <div className='card-header'>
              <h3 className='card-title'>Informer le client</h3>
            </div>
            <div className='card-body'>
              {cloturer &&
              aSolder &&
              (comportement === 'AVOIR' || comportement === 'SANS_EFFET_COMPTABLE') ? (
                <>
                  <SavInformerClient
                    dossier={dossier}
                    evenement={comportement === 'AVOIR' ? 'AVOIR' : null}
                    texteLibre={`Bonjour,\n\nVotre réclamation ${dossier.numero} est acceptée.\n\nLe service SAV – Groupe Surplus Recyclage`}
                    rafraichir={`${total}-${dossier.numeroAvoirAtemo}-${dossier.dateAvoir}`}
                    libelleBouton='Envoyer et clôturer le SAV'
                    avantEnvoi={cloturerAvantMessage}
                    onTermine={() => navigate(`/sav/dossiers/${dossier.id}/fiche`)}
                  />
                  <button
                    className='btn btn-link w-100 mt-2'
                    disabled={envoi || sansDestination.length > 0}
                    onClick={cloturerDossier}
                  >
                    Clôturer sans informer le client
                  </button>
                </>
              ) : (
                <div className='mb-3'>
                  <span className='text-muted'>Mode de contact souhaité : </span>
                  <span className='fw-bold'>
                    {dossier.modeContact === 'MAIL' ? 'Mail' : 'Téléphone'}
                  </span>
                  {comportement === 'ECHANGE' && aSolder && dossier.clientDemandeRemplacement && (
                <div className='rounded p-5 bg-light'>
                  <h4>Échange lancé par la télévente</h4>
                  <SavDemandeRemplacement dossier={dossier} />
                </div>
              )}

              {comportement === 'ECHANGE' && aSolder && !dossier.clientDemandeRemplacement && (
                    <div className='text-muted fs-7 mt-2'>
                      Le message « Échange lancé » est proposé au passage en Commande.
                    </div>
                  )}
                </div>
              )}
              {cloturer && dossier.statut.code === 'COMMANDE' && dossier.modeEchange === 'ATEMO' && (
                <button
                  className='btn btn-success w-100'
                  disabled={envoi}
                  onClick={cloturerDossier}
                >
                  Clôturer le SAV
                </button>
              )}
              <div className='text-muted fs-7 mt-3'>
                Clôture possible quand les données exigées par la solution sont saisies et la
                destination de chaque pièce retournée renseignée.
              </div>
            </div>
          </div>
        </div>
      </div>

      <SavFilModifications detail={detail} onRecharger={maj} />

      {messagePropose && (
        <SavInformerClientModal
          dossier={dossier}
          evenement={messagePropose}
          onClose={fermerMessage}
          onTermine={maj}
        />
      )}
    </>
  )
}

export default SavSolutionPage
